import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormTemplateService } from 'app/services/form-template.service';
import { FormSubmission } from 'app/models/form-submission';
import { finalize } from 'rxjs/operators';
import { FormTemplate } from 'app/models/template';
import { FormFillerComponent } from '../form-filler/form-filler.component';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { UserService } from 'app/services/user.service';
import { ToastService } from 'ng-uikit-pro-standard';

@Component({
  selector: 'app-form-data',
  templateUrl: './form-data.component.html',
  styleUrls: ['./form-data.component.scss']
})
export class FormDataComponent implements OnInit {

  searchKey: string = "";

  isLoadingSubmissions: boolean = true;
  submissions: FormSubmission[] = [];
  filteredSubmissions: FormSubmission[] = [];

  currentFormTemplate: FormTemplate = null;

  canDelete: boolean = false;
  canEdit: boolean = false;

  submissionToDelete: FormSubmission = null;

  spreadsheet_conversion_id: number;

  pendingSubmissionPDFDownloads: number[] = [];

  execsTotalCount = 0;
  pageOffset = 0;

  @ViewChild('formFiller') formFiller: FormFillerComponent;
  @ViewChild('editFormFiller') editFormFiller: FormFillerComponent;

  constructor(private formTemplateService: FormTemplateService, private activatedRoute: ActivatedRoute,
              private router: Router,private ecoDocsTitleService: EcoDocsTitleService,
              private userService: UserService, private toastService: ToastService) {
                this.userService.currentUser.subscribe(user => {
                  if (user) {
                    (window as any).Echo.private(`${user.company_id}.user.${user.id}`).listen('FormSubmissionPDFReady', (e) => {
                      console.log('PDF event: ', e);
                      if (this.pendingSubmissionPDFDownloads.includes(e.submission_id)) {
                        this.formTemplateService.downloadSubmissionPDFById(e.submission_id);
                        this.pendingSubmissionPDFDownloads.splice(this.pendingSubmissionPDFDownloads.indexOf(e.submission_id), 1);
                      }
                    });
                  }
                });
              }

  ngOnInit(): void {
    this.ecoDocsTitleService.setTitle('Forms Explorer > Data', false);
    this.submissions = [];
    this.filteredSubmissions = [];
    this.searchKey = "";
    this.currentFormTemplate = null;
    this.pendingSubmissionPDFDownloads = [];

    this.spreadsheet_conversion_id = this.activatedRoute.snapshot.params.spreadsheet_conversion_id;
    
    this.activatedRoute.queryParams.subscribe(params => {
      this.pageOffset = (params.page || 1) - 1;
      this.loadSubmissions(this.pageOffset + 1);
    });

    this.formTemplateService.getById(this.activatedRoute.snapshot.params.id).subscribe((f) => {
      this.currentFormTemplate = f;
      this.canDelete = this.userService.canForPath('super.admin.data.delete', f.path);
      this.canEdit = this.userService.canForPath('super.admin.data.delete.update', f.path);
    });

    this.loadSubmissions();
  }

  onPageChange(pageInfo) {
    this.loadSubmissions(pageInfo.offset + 1);
  }

  loadSubmissions(pageNumber: number = 1) {
    this.pageOffset = pageNumber - 1;
    // Update the URL's query parameters without refreshing the page
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        "page": (this.pageOffset + 1).toString()
      },
      queryParamsHandling: 'merge', // merge with the current queryParams
      replaceUrl: true // does not add to browser history
    });

    this.isLoadingSubmissions = true;
    let filters: {[key: string]: string|number} = {};
    if (this.spreadsheet_conversion_id) {
      filters.spreadsheet_conversion_id = this.spreadsheet_conversion_id;
    }
    this.formTemplateService.submissions(this.activatedRoute.snapshot.params.id, pageNumber, filters).pipe(finalize(() => {
      this.isLoadingSubmissions = false;
    })).subscribe((r) => {
      this.execsTotalCount = r.total;
      this.pageOffset = r.current_page - 1;
      this.submissions = r.data;
      this.filteredSubmissions = r.data;
    });
  }
  searchSubmissions(val: string){
    this.router.navigate(['/search'], {queryParams: {
      options: JSON.stringify({
          query: val,
          is_files: false,
          is_user_storage: false,
          is_global_storage: false,
          in_file_contents: false,
          is_forms: false,
          in_form_contents: false,
          data_forms: [{id: this.currentFormTemplate.id, path:this.currentFormTemplate.path}],
          is_data: true
      })
    }});
  }

  innerWidth() {
    return window.innerWidth;
  }

  goBack() {
    if (this.spreadsheet_conversion_id) {
      this.router.navigate(['/data-imports']);
    } else {
      this.router.navigate(['/forms-explorer', this.currentFormTemplate.path.substr(0, this.currentFormTemplate.path.lastIndexOf('/'))]);
    }
  }

  onactivate(event: any) {
    if (event.type == "dblclick") {
      const row = <FormSubmission>event.row;

      this.viewSubmission(row);
    }
  }

  viewSubmission(submission: FormSubmission) {
    this.isLoadingSubmissions = true;
    this.formTemplateService.getSubmissionById(submission.id).pipe(finalize(() => {
      this.isLoadingSubmissions = false;
    })).subscribe((s) => {
      this.formFiller.show(s.template, s, null, [], false, true, true);
    });
  }

  generateSubmissionPDF(submission: FormSubmission) {
    this.isLoadingSubmissions = true;
    this.formTemplateService.generateSubmissionPDFById(submission.id).pipe(finalize(() => {
      this.isLoadingSubmissions = false;
    })).subscribe((r) => {
      if (r.status == 'download_ready') {
        this.formTemplateService.downloadSubmissionPDFById(submission.id);
      } else if (r.status == 'will_generate') {
        this.toastService.success('Form Submission PDF is being generated and will download once ready.');
        this.pendingSubmissionPDFDownloads.push(submission.id);
      }
    });
  }

  editSubmission(submission: FormSubmission) {
    this.isLoadingSubmissions = true;
    this.formTemplateService.getSubmissionById(submission.id).pipe(finalize(() => {
      this.isLoadingSubmissions = false;
    })).subscribe((s) => {
      this.editFormFiller.show(s.template, s, s.name, [], false, false, false);
    });
  }

  onEditFormFilled(event: any) {
    this.isLoadingSubmissions = true;
    this.formTemplateService.putSubmission(event.submission).pipe(finalize(() => {
      this.isLoadingSubmissions = false;
      this.loadSubmissions();
    })).subscribe((f) => {
      this.toastService.success('Saved!');
    }, () => {
      this.toastService.error('Error saving data!');
    });
  }

  onDeleteSubmission() {
    this.isLoadingSubmissions = true;
    this.formTemplateService.deleteSubmissionById(this.submissionToDelete.id).pipe(finalize(() => {
      this.isLoadingSubmissions = false;
      this.submissionToDelete = null;
      this.loadSubmissions();
    })).subscribe(() => {
      this.toastService.success('Form Data Deleted!');
    }, () => {
      this.toastService.error('Error deleting form data!');
    });
  }
}
